import { useReport } from '../reporting/ReportContext';
import { FilterBar } from '../reporting/FilterBar';
import { PageHead } from '../reporting/PageHead';
import { KpiCard, Panel } from '../dashboard/ui';
import { SimpleBars } from '../dashboard/charts';
import { CHANNEL_COLORS } from '../dashboard/colors';
import { downloadCsv, km, num, pct } from '../lib/reporting';

const PRIMARY = CHANNEL_COLORS.NVS;

export function DriverPerformance() {
  const { summary, error, filters } = useReport();

  const exportCsv = () => {
    if (!summary) return;
    downloadCsv(
      `nvs-driver-performance-${filters.from}_${filters.to}.csv`,
      summary.byDriver.map((d) => ({
        driver: d.driver,
        stops: d.stops,
        delivered: d.delivered,
        difot: pct(d.difotRate, 2),
        delivered_rate: pct(d.deliveredRate, 2),
        failed: d.failed,
        avg_distance_km: d.avgKm ?? '',
      })),
    );
  };

  const avgDifot =
    summary && summary.byDriver.length
      ? summary.byDriver.reduce((s, d) => s + d.difotRate * d.stops, 0) /
        summary.byDriver.reduce((s, d) => s + d.stops, 0)
      : 0;

  return (
    <section className="report">
      <PageHead
        crumb="Reporting / Driver performance"
        title="Driver performance"
        subtitle={summary ? `NVS in-house · ${summary.from} → ${summary.to} · ${num(summary.totals.drivers)} active drivers` : 'NVS in-house · driver performance'}
      />
      <FilterBar onExport={exportCsv} />

      {error && <p className="form-error" role="alert">{error}</p>}
      {!summary && !error && <p className="muted">Loading…</p>}

      {summary && (
        <>
          <div className="kpi-grid">
            <KpiCard label="Active drivers" value={num(summary.totals.drivers)} sub="With stops in range" accent={PRIMARY} />
            <KpiCard label="Total stops" value={num(summary.totals.stops)} sub="Across all drivers" accent={PRIMARY} />
            <KpiCard label="Avg DIFOT" value={pct(avgDifot)} sub="Volume-weighted" accent={PRIMARY} />
            <KpiCard label="Delivered" value={pct(summary.rates.delivered)} sub={`${num(summary.totals.delivered)} stops`} accent={PRIMARY} />
          </div>

          <Panel title="Top drivers by volume" subtitle="Stops delivered per driver (top 15)">
            <SimpleBars
              data={summary.byDriver.slice(0, 15).map((d) => ({ label: d.driver, value: d.stops }))}
              format={(v) => num(v)}
              color={PRIMARY}
            />
          </Panel>

          <Panel title="All drivers" subtitle={`${num(summary.byDriver.length)} drivers with stops in range`}>
            <div className="data-table-wrap">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Driver</th>
                    <th className="num">Stops</th>
                    <th className="num">Delivered</th>
                    <th className="num">DIFOT</th>
                    <th className="num">Failed</th>
                    <th className="num">Avg distance</th>
                  </tr>
                </thead>
                <tbody>
                  {summary.byDriver.map((d) => (
                    <tr key={d.driver}>
                      <td>{d.driver}</td>
                      <td className="num">{num(d.stops)}</td>
                      <td className="num">{pct(d.deliveredRate, 1)}</td>
                      <td className="num">
                        <span className="util-bar" aria-hidden="true">
                          <span className="util-fill" style={{ width: `${Math.min(100, d.difotRate * 100)}%` }} />
                        </span>
                        {pct(d.difotRate, 1)}
                      </td>
                      <td className={`num ${d.failed > 0 ? 'neg' : ''}`}>{num(d.failed)}</td>
                      <td className="num">{km(d.avgKm)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Panel>
        </>
      )}
    </section>
  );
}
